// @ts-nocheck
import { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";

const isValidMonth = (m) => Number.isInteger(m) && m >= 1 && m <= 12;
const isValidYear = (y) => Number.isInteger(y) && y >= 2000 && y <= 2100;

const normalize = ({ month, year }) => {
  const m = parseInt(String(month), 10);
  const y = parseInt(String(year), 10);
  if (!isValidMonth(m) || !isValidYear(y)) return null;
  return { month: m, year: y };
};

/**
 * Single source of truth for V2 period selection (month/year).
 * - Persists to URL (?month=&year=) so navigation keeps the selection
 */
export function useV2MonthYear() {
  const location = useLocation();
  const navigate = useNavigate();
  const now = useMemo(() => new Date(), []);
  const defaultValue = useMemo(
    () => ({ month: now.getMonth() + 1, year: now.getFullYear() }),
    [now]
  );

  const parseSearch = () => {
    const params = new URLSearchParams(location.search);
    const month = params.get("month");
    const year = params.get("year");
    if (!month || !year) return null;
    return normalize({ month, year });
  };

  const initialSourceRef = useRef(null);
  const [value, setValue] = useState(() => {
    const fromUrl = parseSearch();
    if (fromUrl) {
      initialSourceRef.current = "url";
      return fromUrl;
    }
    initialSourceRef.current = "default";
    return defaultValue;
  });

  const ensureUrlHasValue = (v) => {
    const params = new URLSearchParams(location.search);
    const currentMonth = params.get("month");
    const currentYear = params.get("year");
    const wants = { month: String(v.month), year: String(v.year) };
    if (currentMonth === wants.month && currentYear === wants.year) return;

    params.set("month", wants.month);
    params.set("year", wants.year);
    navigate({ pathname: location.pathname, search: `?${params.toString()}` }, { replace: true });
  };

  // If URL changes (back/forward/manual edits), sync local state.
  useEffect(() => {
    const fromUrl = parseSearch();
    if (!fromUrl) return;
    if (fromUrl.month === value.month && fromUrl.year === value.year) return;
    setValue(fromUrl);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search]);

  // On mount (and when pathname changes), ensure we always have ?month&year in URL
  // so sidebar / internal links can preserve the period across pages.
  useEffect(() => {
    const fromUrl = parseSearch();
    if (fromUrl) return;
    ensureUrlHasValue(value);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  const setMonthYear = ({ month, year }, { replace = true } = {}) => {
    const next = normalize({ month, year });
    if (!next) return;

    setValue(next);

    const params = new URLSearchParams(location.search);
    params.set("month", String(next.month));
    params.set("year", String(next.year));
    const nextSearch = `?${params.toString()}`;

    if (nextSearch !== location.search) {
      navigate({ pathname: location.pathname, search: nextSearch }, { replace });
    }
  };

  return {
    month: value.month,
    year: value.year,
    setMonthYear,
    initialSource: initialSourceRef.current, // "url" | "default"
  };
}


